import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Sun, Moon, Monitor, User as UserIcon, Globe, Palette } from "lucide-react";
import CountryFlag from "@/components/ui/CountryFlag";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLanguage, languages, type LanguageCode } from "@/contexts/LanguageContext";
import { useTheme, type Theme } from "@/contexts/ThemeContext";
import { useTranslation } from "@/i18n/translations";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentUserProfileQuery, useUpdateUserSettingsMutation } from "@/hooks/queries/useUserSettingsQuery";
import { toast } from "sonner";

const settingsSchema = z.object({
  displayName: z.string().max(255, "Display name must not exceed 255 characters").optional(),
});

type SettingsFormValues = z.infer<typeof settingsSchema>;

const Settings = () => {
  const navigate = useNavigate();
  const { language, setLanguage, currentLanguage } = useLanguage();
  const { t } = useTranslation(language);
  const { theme, setTheme } = useTheme();
  const { user, setAuthUser } = useAuth();

  const { data: profile, isLoading } = useCurrentUserProfileQuery();
  const updateSettings = useUpdateUserSettingsMutation();

  const [selectedLanguage, setSelectedLanguage] = useState<LanguageCode>(language);
  const [selectedTheme, setSelectedTheme] = useState<Theme>(theme);

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<SettingsFormValues>({
    resolver: zodResolver(settingsSchema),
    defaultValues: { displayName: "" },
  });

  // Populate form once profile is loaded
  useEffect(() => {
    if (profile) {
      setValue("displayName", profile.nickName ?? "");
      if (profile.language) {
        const code = profile.language as LanguageCode;
        setSelectedLanguage(code);
      }
      if (profile.theme) {
        setSelectedTheme(profile.theme as Theme);
      }
    }
  }, [profile, setValue]);

  const onSubmit = async (values: SettingsFormValues) => {
    if (!profile) return;

    try {
      const updated = await updateSettings.mutateAsync({
        userId: profile.id,
        settings: {
          displayName: values.displayName || undefined,
          theme: selectedTheme,
          language: selectedLanguage,
        },
      });

      // Apply theme and language immediately
      setTheme(selectedTheme);
      setLanguage(selectedLanguage);

      // Update auth user state to reflect new display name
      if (user) {
        setAuthUser({ ...user, username: updated.nickName ?? user.username });
      }

      toast.success(t("settingsSaved"), { description: t("settingsSavedDesc") });
    } catch {
      toast.error(t("settingsError"));
    }
  };

  const themeOptions: { value: Theme; label: string; icon: React.ReactNode }[] = [
    { value: "light", label: t("themeLight"), icon: <Sun size={16} /> },
    { value: "dark", label: t("themeDark"), icon: <Moon size={16} /> },
    { value: "system", label: t("themeSystem"), icon: <Monitor size={16} /> },
  ];

  const currentThemeOption = themeOptions.find((o) => o.value === selectedTheme) ?? themeOptions[2];
  const selectedLangObj = languages.find((l) => l.code === selectedLanguage) ?? languages[0];

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-bold">{t("settingsTitle")}</h1>
          <p className="text-muted-foreground mt-1">{t("settingsSubtitle")}</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Profile Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserIcon size={18} />
                {t("profileSettings")}
              </CardTitle>
              <CardDescription>{user?.email}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="displayName">{t("displayName")}</Label>
                {isLoading ? (
                  <div className="h-10 bg-muted animate-pulse rounded-md" />
                ) : (
                  <Input
                    id="displayName"
                    placeholder={t("displayNamePlaceholder")}
                    {...register("displayName")}
                  />
                )}
                {errors.displayName && (
                  <p className="text-sm text-destructive">{errors.displayName.message}</p>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Appearance Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Palette size={18} />
                {t("themeSettings")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {themeOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSelectedTheme(option.value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
                      selectedTheme === option.value
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background text-foreground border-border hover:bg-muted"
                    }`}
                  >
                    {option.icon}
                    {option.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Language Section */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe size={18} />
                {t("languageSettings")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <CountryFlag countryCode={selectedLangObj.flag} />
                    <span>{selectedLangObj.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="bg-background border border-border shadow-lg z-50">
                  {languages.map((lang) => (
                    <DropdownMenuItem
                      key={lang.code}
                      onClick={() => setSelectedLanguage(lang.code)}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <CountryFlag countryCode={lang.flag} />
                      <span>{lang.name}</span>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </CardContent>
          </Card>

          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              disabled={updateSettings.isPending || isLoading}
            >
              {updateSettings.isPending ? t("save") + "..." : t("save")}
            </Button>
          </div>
        </form>
      </div>
    </DashboardLayout>
  );
};

export default Settings;
